/**
 * EditorPropertyPanel: DOM form for editing the selected object's basic
 * properties — name, transform (position/rotation/scale), marker color (for
 * primitives), and interaction mode/radius.
 *
 * Edits write straight back to the live `SceneObject` (via the mutators) and
 * refresh the marker/selection so changes are immediately visible. The panel
 * only re-renders when the *selection* changes; field inputs write back without
 * re-rendering (to keep focus), and signal `changed()` so the object list and
 * selection label stay in sync.
 */

import type { SceneObject, InteractionMode, SceneObjectColliderShape } from '../scene/SceneObjectTypes';
import type { EditorState } from './EditorState';
import type { SceneObjectManager } from '../scene/SceneObjectManager';
import type { SceneSkyboxConfig } from '../config/manifest';
import { EditorEventPanel } from './EditorEventPanel';
import { EditorPortalPanel } from './EditorPortalPanel';

/** Cached list of appearance models in /assets/models/ (via dev backend). */
let _modelListCache: string[] | null = null;
let _modelListPromise: Promise<string[]> | null = null;
function getModelList(): Promise<string[]> {
    if (_modelListCache) return Promise.resolve(_modelListCache);
    if (!_modelListPromise) {
        _modelListPromise = fetch('/api/assets/models')
            .then((r) => (r.ok ? r.json() : []))
            .then((files: string[]) => {
                _modelListCache = (files || []).map((f) => f.startsWith('/assets/') ? f : `/assets/models/${f}`);
                return _modelListCache;
            })
            .catch(() => {
                _modelListCache = [];
                return [];
            });
    }
    return _modelListPromise;
}

/** Cached list of panoramic videos in /assets/sky/videos/ (via dev backend). */
let _skyVideoListCache: string[] | null = null;
let _skyVideoListPromise: Promise<string[]> | null = null;
function getSkyVideoList(): Promise<string[]> {
    if (_skyVideoListCache) return Promise.resolve(_skyVideoListCache);
    if (!_skyVideoListPromise) {
        _skyVideoListPromise = fetch('/api/assets/sky/videos')
            .then((r) => (r.ok ? r.json() : []))
            .then((files: string[]) => {
                _skyVideoListCache = (files || []).map((f) => f.startsWith('/assets/') ? f : `/assets/sky/videos/${f}`);
                return _skyVideoListCache;
            })
            .catch(() => {
                _skyVideoListCache = [];
                return [];
            });
    }
    return _skyVideoListPromise;
}

/** Cached list of videos in /assets/videos/ (via dev backend). */
let _videoListCache: string[] | null = null;
let _videoListPromise: Promise<string[]> | null = null;
function getVideoList(): Promise<string[]> {
    if (_videoListCache) return Promise.resolve(_videoListCache);
    if (!_videoListPromise) {
        _videoListPromise = fetch('/api/assets/videos')
            .then((r) => (r.ok ? r.json() : []))
            .then((files: string[]) => {
                _videoListCache = (files || []).map((f) => f.startsWith('/assets/') ? f : `/assets/videos/${f}`);
                return _videoListCache;
            })
            .catch(() => {
                _videoListCache = [];
                return [];
            });
    }
    return _videoListPromise;
}

/** Cached list of poster/default images in /assets/images/ (via dev backend). */
let _imageListCache: string[] | null = null;
let _imageListPromise: Promise<string[]> | null = null;
function getImageList(): Promise<string[]> {
    if (_imageListCache) return Promise.resolve(_imageListCache);
    if (!_imageListPromise) {
        _imageListPromise = fetch('/api/assets/images')
            .then((r) => (r.ok ? r.json() : []))
            .then((files: string[]) => {
                _imageListCache = (files || []).map((f) => f.startsWith('/assets/') ? f : `/assets/images/${f}`);
                return _imageListCache;
            })
            .catch(() => {
                _imageListCache = [];
                return [];
            });
    }
    return _imageListPromise;
}

type SkyboxListItem = {
    label: string;
    config: SceneSkyboxConfig;
};

/** Cached list of skyboxes in /assets/sky/ (via dev backend). */
let _skyboxListCache: SkyboxListItem[] | null = null;
let _skyboxListPromise: Promise<SkyboxListItem[]> | null = null;
function toCubemapSkyboxConfig(root: string): SceneSkyboxConfig {
    const base = root.replace(/\/+$/, '');
    return {
        type: 'cubemap',
        faces: {
            px: `${base}/px.png`,
            nx: `${base}/nx.png`,
            py: `${base}/py.png`,
            ny: `${base}/ny.png`,
            pz: `${base}/pz.png`,
            nz: `${base}/nz.png`
        }
    };
}

function skyboxCubemapLabel(faces: { px: string }): string {
    const match = faces.px.match(/\/assets\/sky\/(.+)\/[^/]+$/);
    return match?.[1] ?? faces.px.split('/').slice(-2, -1)[0] ?? 'cubemap';
}

function clampSkyboxRotationYaw(value: number): number {
    return Math.max(0, Math.min(360, value));
}

function normalizeSkyboxListItem(value: unknown): SkyboxListItem | null {
    if (typeof value === 'string') {
        if (/\.dds$/i.test(value)) {
            const url = value.startsWith('/assets/') ? value : `/assets/sky/${value}`;
            return { label: url.split('/').pop() || url, config: { type: 'texture', url } };
        }
        const root = value.startsWith('/assets/') ? value : `/assets/sky/${value}`;
        return { label: root.split('/').pop() || root, config: toCubemapSkyboxConfig(root) };
    }
    if (!value || typeof value !== 'object') return null;
    const item = value as { label?: unknown; type?: unknown; url?: unknown; faces?: unknown };
    if (item.type === 'texture' && typeof item.url === 'string') {
        return {
            label: typeof item.label === 'string' ? item.label : item.url.split('/').pop() || item.url,
            config: { type: 'texture', url: item.url }
        };
    }
    if (item.type === 'cubemap' && item.faces && typeof item.faces === 'object') {
        const faces = item.faces as Record<string, unknown>;
        if (['px', 'nx', 'py', 'ny', 'pz', 'nz'].every((k) => typeof faces[k] === 'string')) {
            return {
                label: typeof item.label === 'string' ? item.label : skyboxCubemapLabel({ px: faces.px as string }),
                config: {
                    type: 'cubemap',
                    faces: {
                        px: faces.px as string,
                        nx: faces.nx as string,
                        py: faces.py as string,
                        ny: faces.ny as string,
                        pz: faces.pz as string,
                        nz: faces.nz as string
                    }
                }
            };
        }
    }
    return null;
}

function skyboxOptionValue(config: SceneSkyboxConfig): string {
    if (config.type === 'texture') return `texture:${config.url}`;
    if (config.type === 'video') return `video:${config.url}`;
    return `cubemap:${config.faces.px}`;
}

function getSkyboxList(): Promise<SkyboxListItem[]> {
    if (_skyboxListCache) return Promise.resolve(_skyboxListCache);
    if (!_skyboxListPromise) {
        _skyboxListPromise = fetch('/api/assets/skyboxes')
            .then((r) => (r.ok ? r.json() : []))
            .then((items: unknown[]) => {
                _skyboxListCache = (items || [])
                    .map(normalizeSkyboxListItem)
                    .filter((item): item is SkyboxListItem => !!item);
                return _skyboxListCache;
            })
            .catch(() => {
                _skyboxListCache = [];
                return [];
            });
    }
    return _skyboxListPromise;
}

function markerUniformScale(marker: SceneObject['marker']): number {
    if (marker.kind === 'primitive' || marker.kind === 'video' || marker.kind === 'imagePanel') {
        return (marker.scale ?? [1, 1, 1])[0];
    }
    return marker.scale ?? 1;
}

function markerScaleVec(marker: SceneObject['marker']): [number, number, number] {
    if (marker.kind === 'primitive' || marker.kind === 'video' || marker.kind === 'imagePanel') {
        return marker.scale ?? [1, 1, 1];
    }
    const s = marker.scale ?? 1;
    return [s, s, s];
}

export interface PropertyMutators {
    setTransform(id: string, pos: [number, number, number], rot: [number, number, number]): void;
    /** Rebuild the marker entity + selection visuals after a marker/transform edit. */
    refresh(id: string): void;
    /** Mutate the live object's data in place. */
    mutate(id: string, fn: (o: SceneObject) => void): void;
    /** Notify other panels (object list, label) that something changed. */
    changed(): void;
    /** Fire a single event action as the player would (event panel 测试). */
    testEvent(id: string, eventIndex: number): void;
    /** Fire PORTAL_ENTER for a portal object (portal panel 测试传送). */
    testPortal(id: string): void;
    /** Registry scene ids — populates the portal target-scene dropdown. */
    getScenes(): string[];
    /** Current scene spawn pose, shown when no object is selected. */
    getSceneSpawn(): { spawn: [number, number, number]; spawnAngles: [number, number] } | null;
    /** Update the current scene spawn pose. */
    setSceneSpawn(spawn: [number, number, number], spawnAngles: [number, number]): void;
    /** Current scene skybox config, shown when no object is selected. */
    getSceneSkybox(): SceneSkyboxConfig | null;
    /** Update the current scene skybox config. */
    setSceneSkybox(skybox: SceneSkyboxConfig | null): void;
    /** Current mobile scene skybox config, shown when no object is selected. */
    getSceneMobileSkybox(): SceneSkyboxConfig | null;
    /** Update the mobile scene skybox config. */
    setSceneMobileSkybox(skybox: SceneSkyboxConfig | null): void;
}

const MODES: InteractionMode[] = ['enter', 'click', 'lookAt', 'auto'];
const IMAGE_PANEL_OPTION = '__imagePanel';
const DEFAULT_IMAGE_PANEL_SCALE: [number, number, number] = [4, 2.25, 1];
const DEFAULT_IMAGE_PANEL_ROTATION_SPEED = 30;
const COLLIDER_SHAPES: SceneObjectColliderShape[] = ['box', 'sphere', 'cylinder', 'capsule'];
const COLLIDER_SHAPE_OPTIONS: Array<{ value: 'none' | SceneObjectColliderShape; label: string }> = [
    { value: 'none', label: '无' },
    { value: 'box', label: '盒子碰撞' },
    { value: 'sphere', label: '球形碰撞' },
    { value: 'cylinder', label: '圆柱碰撞' },
    { value: 'capsule', label: '胶囊碰撞' }
];
type PrimitiveMarker = Extract<SceneObject['marker'], { kind: 'primitive' }>;
type ImagePanelMarker = Extract<SceneObject['marker'], { kind: 'imagePanel' }>;
type HotspotMarkerPreset = {
    id: string;
    label: string;
    shape: PrimitiveMarker['shape'];
    color: [number, number, number];
    scale: [number, number, number];
    opacity: number;
    rotation?: [number, number, number];
};

const HOTSPOT_MARKER_PRESETS: HotspotMarkerPreset[] = [
    { id: 'glow-sphere', label: '发光球', shape: 'sphere', color: [0.4, 1, 0.5], scale: [0.6, 0.6, 0.6], opacity: 0.5 },
    { id: 'beacon-column', label: '光柱', shape: 'cylinder', color: [0.35, 0.85, 1], scale: [0.35, 1.4, 0.35], opacity: 0.35 },
    { id: 'flat-panel', label: '平面标牌', shape: 'plane', color: [0.4, 1, 0.5], scale: [1.2, 1.2, 1], opacity: 0.65 },
    { id: 'flat-ground', label: '平面地贴', shape: 'plane', color: [1, 0.85, 0.25], scale: [1.4, 1.4, 1], opacity: 0.45, rotation: [90, 0, 0] }
];

function hotspotMarkerPresetId(marker: SceneObject['marker']): string {
    if (marker.kind !== 'primitive') return '';
    const scale = marker.scale ?? [1, 1, 1];
    const preset = HOTSPOT_MARKER_PRESETS.find((p) =>
        p.shape === marker.shape &&
        p.scale.every((v, i) => Math.abs(v - scale[i]) < 1e-4)
    );
    return preset?.id ?? '';
}

function defaultColliderForShape(shape: SceneObjectColliderShape, scale: [number, number, number]): NonNullable<SceneObject['collider']> {
    const maxScale = Math.max(Math.abs(scale[0]), Math.abs(scale[1]), Math.abs(scale[2]));
    const radius = Math.max(Math.max(Math.abs(scale[0]), Math.abs(scale[2])) * 0.5, 0.05);
    const height = Math.max(Math.abs(scale[1]), radius * 2);
    if (shape === 'box') return { shape, center: [0, 0, 0], size: [...scale] as [number, number, number] };
    if (shape === 'sphere') return { shape, center: [0, 0, 0], radius: Math.max(maxScale * 0.5, 0.05) };
    return { shape, center: [0, 0, 0], radius, height };
}

export class EditorPropertyPanel {
    private _mount: HTMLElement;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;
    private _mut: PropertyMutators;
    private _renderedId: string | null | undefined = undefined;
    private _renderedSceneSettingsVersion = -1;
    private _unsubscribe: () => void;
    private _unsubscribeTransform: () => void;

    /** Input element handles for the transform rows, so a gizmo drag / undo /
     *  redo can update the displayed values in place (without re-rendering and
     *  stealing focus from another field). */
    private _posInputs: HTMLInputElement[] = [];
    private _rotInputs: HTMLInputElement[] = [];
    private _scaleInputs: HTMLInputElement[] = [];

    /** Sub-panels for events + portal config, recreated when selection changes. */
    private _eventPanel: EditorEventPanel | null = null;
    private _portalPanel: EditorPortalPanel | null = null;

    constructor(
        mount: HTMLElement,
        state: EditorState,
        getMgr: () => SceneObjectManager | null,
        mutators: PropertyMutators
    ) {
        this._mount = mount;
        this._state = state;
        this._getMgr = getMgr;
        this._mut = mutators;

        this._unsubscribe = state.subscribe(() => this._onStateChanged());
        this._unsubscribeTransform = state.subscribeTransform(() => this._refreshValues());
        this._onStateChanged();
    }

    private _onStateChanged(): void {
        const selectedId = this._state.selectedId;
        if (selectedId === null) {
            const staleScenePanel = this._renderedSceneSettingsVersion !== this._state.sceneSettingsVersion;
            if (this._renderedId !== null || staleScenePanel) this._render();
            return;
        }

        // Only re-render object panels when the selection changed.
        if (selectedId !== this._renderedId) {
            this._render();
        }
    }

    private _render(): void {
        const id = this._state.selectedId;
        this._renderedId = id;
        // Tear down last selection's sub-panels before rebuilding.
        this._eventPanel?.destroy();
        this._eventPanel = null;
        this._portalPanel?.destroy();
        this._portalPanel = null;
        this._mount.innerHTML = '';

        if (!id) {
            this._renderedSceneSettingsVersion = this._state.sceneSettingsVersion;
            this._renderSceneSpawn();
            return;
        }

        const mgr = this._getMgr();
        const o = mgr?.get(id);
        if (!o) {
            this._renderedId = null;
            return;
        }

        const root = document.createElement('div');
        root.className = 'ep-root';

        // Reset live-input handles for the new selection.
        this._posInputs = [];
        this._rotInputs = [];
        this._scaleInputs = [];

        root.appendChild(this._header(o));
        root.appendChild(this._section('名称', this._text(o.name, (v) => {
            this._mut.mutate(id, (x) => { x.name = v; });
            this._mut.changed();
            this._updateHeaderName(v);
        })));

        root.appendChild(this._section('位置 (X / Y / Z)', this._vec3(o.transform.position, 0.1, (v) => {
            const cur = this._getMgr()?.get(id);
            this._mut.setTransform(id, v, cur?.transform.rotation ?? o.transform.rotation);
            this._mut.changed();
        }, this._posInputs)));

        root.appendChild(this._section('旋转 (Pitch / Yaw / Roll)', this._vec3(o.transform.rotation, 1, (v) => {
            const cur = this._getMgr()?.get(id);
            this._mut.setTransform(id, cur?.transform.position ?? o.transform.position, v);
            this._mut.changed();
        }, this._rotInputs)));

        root.appendChild(this._section('缩放 (X / Y / Z)', this._vec3(o.transform.scale, 0.1, (v) => {
            this._mut.mutate(id, (x) => {
                x.transform.scale = v;
                if (x.marker.kind === 'primitive' || x.marker.kind === 'video' || x.marker.kind === 'imagePanel') x.marker.scale = v;
                else if (x.marker.kind === 'glb' || x.marker.kind === 'gsplat' || x.marker.kind === 'sprite') x.marker.scale = v[0];
                if (x.type === 'triggerZone' && x.trigger) x.trigger.size = v;
            });
            this._mut.refresh(id);
            this._mut.changed();
        }, this._scaleInputs)));

        this._appendDivider(root);
        this._renderColliderConfig(root, id, o);

        if (o.type === 'triggerZone') {
            root.appendChild(this._section('触发体积', this._hint('用顶部「缩放」工具拖动调整盒体范围')));
        }

        if (o.type === 'hotspot') {
            root.appendChild(this._section('热点样式', this._hotspotStyleSelect(id, o)));
        }

        const isPrim = o.marker.kind === 'primitive';
        if (isPrim && o.marker.kind === 'primitive') {
            const col = o.marker.color ?? [1, 1, 1];
            root.appendChild(this._section('颜色 (R / G / B)', this._vec3(col, 0.01, (v) => {
                this._mut.mutate(id, (x) => {
                    if (x.marker.kind === 'primitive') x.marker.color = v;
                });
                this._mut.refresh(id);
            })));
            root.appendChild(this._section('透明度', this._num(o.marker.opacity ?? 1, 0.05, (v) => {
                const opacity = Math.max(0, Math.min(1, v));
                this._mut.mutate(id, (x) => {
                    if (x.marker.kind === 'primitive') x.marker.opacity = opacity;
                });
                this._mut.refresh(id);
                this._mut.changed();
            })));
        }

        // 外观模型: dropdown of models in /assets/models/ (listed via index.json).
        // "— 基础形状 —" reverts to a primitive. Kind is auto-detected from the
        // URL extension (.sog / .lod-meta.json / .ply / .splat → gsplat, else glb).
        // Portal and triggerZone also offer a "面板模型" (imagePanel) option.
        {
            const supportsImagePanel = o.type === 'portal' || o.type === 'triggerZone';
            const curUrl = o.marker.kind === 'glb' || o.marker.kind === 'gsplat' ? o.marker.url : '';
            const selectedAppearance = o.marker.kind === 'imagePanel' ? IMAGE_PANEL_OPTION : curUrl;
            const sel = document.createElement('select');
            sel.className = 'ep-input ep-grow';
            const noneOpt = document.createElement('option');
            noneOpt.value = '';
            noneOpt.textContent = '— 基础形状 —';
            sel.appendChild(noneOpt);
            if (supportsImagePanel) {
                const panelOpt = document.createElement('option');
                panelOpt.value = IMAGE_PANEL_OPTION;
                panelOpt.textContent = '面板模型';
                panelOpt.selected = o.marker.kind === 'imagePanel';
                sel.appendChild(panelOpt);
            }
            // Show the current value immediately even before the list loads (or
            // if it's a custom URL not in the manifest).
            if (curUrl) {
                const cur = document.createElement('option');
                cur.value = curUrl;
                cur.textContent = curUrl.split('/').pop() || curUrl;
                cur.selected = true;
                sel.appendChild(cur);
            }
            getModelList().then((urls) => {
                for (const u of urls) {
                    if (u === curUrl) continue;
                    const opt = document.createElement('option');
                    opt.value = u;
                    opt.textContent = u.split('/').pop() || u;
                    sel.appendChild(opt);
                }
                sel.value = selectedAppearance || '';
            });
            sel.value = selectedAppearance || '';
            sel.addEventListener('change', () => {
                const url = sel.value;
                this._mut.mutate(id, (x) => {
                    if (!url) {
                        // Revert to primitive box.
                        const s = markerUniformScale(x.marker);
                        x.marker = { kind: 'primitive', shape: 'box', color: [1, 0.85, 0.3], scale: [s, s, s], opacity: 0.5 };
                        x.transform.scale = [s, s, s];
                        return;
                    }
                    if (url === IMAGE_PANEL_OPTION && (x.type === 'portal' || x.type === 'triggerZone')) {
                        const prev = x.marker;
                        const scale = prev.kind === 'imagePanel'
                            ? markerScaleVec(prev)
                            : (x.type === 'triggerZone' && x.trigger
                                ? [...x.trigger.size] as [number, number, number]
                                : [...DEFAULT_IMAGE_PANEL_SCALE] as [number, number, number]);
                        x.marker = {
                            kind: 'imagePanel',
                            url: prev.kind === 'imagePanel' ? prev.url : '',
                            scale: [...scale] as [number, number, number],
                            autoRotate: prev.kind === 'imagePanel' ? prev.autoRotate : false,
                            rotationSpeed: prev.kind === 'imagePanel' ? prev.rotationSpeed : DEFAULT_IMAGE_PANEL_ROTATION_SPEED,
                            rotationAxis: prev.kind === 'imagePanel' ? prev.rotationAxis : 'y'
                        };
                        x.transform.scale = [...scale] as [number, number, number];
                        if (prev.kind !== 'imagePanel') x.transform.rotation = [90, x.transform.rotation[1], 0];
                        return;
                    }
                    const isGsplat = /\.(sog|ply|splat)(\?.*)?$/i.test(url) || /lod-meta\.json(\?.*)?$/i.test(url);
                    const s = markerUniformScale(x.marker);
                    x.marker = isGsplat
                        ? { kind: 'gsplat', url, scale: s }
                        : { kind: 'glb', url, scale: s };
                    x.transform.scale = [s, s, s];
                });
                this._mut.refresh(id);
                this._mut.changed();
                this._render();
            });
            root.appendChild(this._section('外观模型', sel));
        }

        if ((o.type === 'portal' || o.type === 'triggerZone') && o.marker.kind === 'imagePanel') {
            this._renderImagePanelMarker(root, id, o);
        }

        if (o.interaction && o.type !== 'portal') this._appendDivider(root);

        if (o.type === 'videoScreen') {
            this._renderVideoScreen(root, id, o);
        }

        // Portals auto-fire PORTAL_ENTER on enter — no event/interaction config.
        if (o.interaction && o.type !== 'portal') {
            const modeRow = document.createElement('div');
            modeRow.className = 'ep-field';
            const sel = document.createElement('select');
            for (const m of MODES) {
                const opt = document.createElement('option');
                opt.value = m; opt.textContent = m;
                if (m === o.interaction.mode) opt.selected = true;
                sel.appendChild(opt);
            }
            sel.addEventListener('change', () => {
                const m = sel.value as InteractionMode;
                this._mut.mutate(id, (x) => { if (x.interaction) x.interaction.mode = m; });
                this._mut.refresh(id);
                this._mut.changed();
            });
            modeRow.appendChild(this._label('触发模式'));
            modeRow.appendChild(sel);
            root.appendChild(modeRow);

            if (o.type === 'triggerZone') {
                // Trigger zones use a box volume (trigger.size, driven by 缩放
                // above); interaction.radius is ignored at runtime, so show a
                // hint instead of a misleading radius input.
                const hint = document.createElement('div');
                hint.className = 'ep-empty';
                hint.textContent = '盒体触发：范围由上方「缩放 / 区域尺寸」决定';
                root.appendChild(this._section('触发体积', hint));
            } else {
                root.appendChild(this._section('触发半径', this._num(o.interaction.radius ?? 3, 0.1, (v) => {
                    this._mut.mutate(id, (x) => { if (x.interaction) x.interaction.radius = v; });
                    this._mut.refresh(id);
                    this._mut.changed();
                })));
            }
        }

        // Event actions sub-panel (objects with an interaction; not portals).
        if (o.interaction && o.type !== 'portal') {
            const evMount = document.createElement('div');
            root.appendChild(this._section('事件', evMount));
            this._eventPanel = new EditorEventPanel(evMount, this._state, this._getMgr, this._mut);
        }

        // Portal config sub-panel (portal objects).
        if (o.type === 'portal') {
            this._appendDivider(root);
            const portalMount = document.createElement('div');
            root.appendChild(this._section('传送门', portalMount));
            this._portalPanel = new EditorPortalPanel(portalMount, this._state, this._getMgr, this._mut);
        }

        this._mount.appendChild(root);
    }

    private _hotspotStyleSelect(id: string, o: SceneObject): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';

        const custom = document.createElement('option');
        custom.value = '';
        custom.textContent = '自定义';
        sel.appendChild(custom);

        for (const preset of HOTSPOT_MARKER_PRESETS) {
            const opt = document.createElement('option');
            opt.value = preset.id;
            opt.textContent = preset.label;
            sel.appendChild(opt);
        }
        sel.value = hotspotMarkerPresetId(o.marker);

        sel.addEventListener('change', () => {
            const preset = HOTSPOT_MARKER_PRESETS.find((p) => p.id === sel.value);
            if (!preset) return;
            this._mut.mutate(id, (x) => {
                x.marker = {
                    kind: 'primitive',
                    shape: preset.shape,
                    color: [...preset.color] as [number, number, number],
                    scale: [...preset.scale] as [number, number, number],
                    opacity: preset.opacity
                };
                x.transform.scale = [...preset.scale] as [number, number, number];
                if (preset.rotation) x.transform.rotation = [...preset.rotation] as [number, number, number];
            });
            this._mut.refresh(id);
            this._mut.changed();
            this._render();
        });

        return sel;
    }

    private _renderColliderConfig(root: HTMLElement, id: string, o: SceneObject): void {
        const collider = o.collider;
        root.appendChild(this._section('碰撞体', this._colliderShapeSelect(collider?.shape ?? 'none', (shape) => {
            this._mut.mutate(id, (x) => {
                if (shape === 'none') {
                    delete x.collider;
                    return;
                }
                x.collider = defaultColliderForShape(shape, markerScaleVec(x.marker));
            });
            this._mut.refresh(id);
            this._mut.changed();
            this._render();
        })));

        if (!collider) return;

        // 碰撞体类型对象的碰撞尺寸/中心/旋转始终跟随物体变换（缩放、位置、旋转），
        // 无需也无法手动调整，仅显示提示。
        if (o.type === 'collider') {
            root.appendChild(this._section('碰撞同步', this._hint('碰撞区域自动跟随缩放、旋转、位置')));
            return;
        }

        const setCollider = (fn: (v: NonNullable<SceneObject['collider']>) => void) => {
            this._mut.mutate(id, (x) => {
                if (!x.collider) return;
                fn(x.collider);
            });
            this._mut.refresh(id);
            this._mut.changed();
        };

        root.appendChild(this._section('碰撞中心 (X / Y / Z)', this._vec3(collider.center ?? [0, 0, 0], 0.05, (v) => {
            setCollider((cfg) => { cfg.center = v; });
        })));

        if (collider.shape === 'box') {
            root.appendChild(this._section('碰撞尺寸 (X / Y / Z)', this._vec3(collider.size ?? markerScaleVec(o.marker), 0.1, (v) => {
                setCollider((cfg) => { cfg.size = v; });
            })));
            return;
        }

        if (collider.shape === 'sphere') {
            root.appendChild(this._section('碰撞半径', this._num(collider.radius ?? Math.max(...markerScaleVec(o.marker)) * 0.5, 0.05, (v) => {
                setCollider((cfg) => { cfg.radius = Math.max(0.05, v); });
            })));
            return;
        }

        if (collider.shape === 'cylinder' || collider.shape === 'capsule') {
            const scale = markerScaleVec(o.marker);
            root.appendChild(this._section('碰撞半径', this._num(collider.radius ?? Math.max(scale[0], scale[2]) * 0.5, 0.05, (v) => {
                setCollider((cfg) => { cfg.radius = Math.max(0.05, v); });
            })));
            root.appendChild(this._section('碰撞高度', this._num(collider.height ?? scale[1], 0.1, (v) => {
                setCollider((cfg) => { cfg.height = Math.max(0.05, v); });
            })));
        }
    }

    private _colliderShapeSelect(value: 'none' | SceneObjectColliderShape, on: (v: 'none' | SceneObjectColliderShape) => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';
        for (const item of COLLIDER_SHAPE_OPTIONS) {
            const opt = document.createElement('option');
            opt.value = item.value;
            opt.textContent = item.label;
            sel.appendChild(opt);
        }
        sel.value = COLLIDER_SHAPES.includes(value as SceneObjectColliderShape) ? value : 'none';
        sel.addEventListener('change', () => {
            const value = sel.value;
            if (value === 'none' || COLLIDER_SHAPES.includes(value as SceneObjectColliderShape)) {
                on(value as 'none' | SceneObjectColliderShape);
            }
        });
        return sel;
    }

    private _renderVideoScreen(root: HTMLElement, id: string, o: SceneObject): void {
        const video = o.video ?? { url: '', mobileUrl: '', posterUrl: '', autoplay: false, defaultVisible: true, loop: true, muted: true, volume: 1, emissiveIntensity: 1.8 };
        const setVideo = (fn: (v: NonNullable<SceneObject['video']>) => void) => {
            this._mut.mutate(id, (x) => {
                x.video ??= { url: '', mobileUrl: '', posterUrl: '', autoplay: false, defaultVisible: true, loop: true, muted: true, volume: 1, emissiveIntensity: 1.8 };
                fn(x.video);
            });
            this._mut.refresh(id);
            this._mut.changed();
        };

        root.appendChild(this._section('视频地址', this._videoSelect(video.url, (v) => {
            setVideo((cfg) => { cfg.url = v; cfg.mobileUrl = v; });
        })));
        root.appendChild(this._section('移动端视频地址', this._videoSelect(video.mobileUrl ?? video.url, (v) => {
            setVideo((cfg) => {
                if (v) cfg.mobileUrl = v;
                else delete cfg.mobileUrl;
            });
        })));
        root.appendChild(this._section('默认图地址', this._imageSelect(video.posterUrl ?? '', (v) => {
            setVideo((cfg) => {
                if (v) cfg.posterUrl = v;
                else delete cfg.posterUrl;
            });
        })));
        root.appendChild(this._section('默认显示', this._checkbox(video.defaultVisible ?? true, (v) => {
            setVideo((cfg) => { cfg.defaultVisible = v; });
        })));
        root.appendChild(this._section('循环播放', this._checkbox(video.loop ?? true, (v) => {
            setVideo((cfg) => { cfg.loop = v; });
        })));
        root.appendChild(this._section('静音', this._checkbox(video.muted ?? true, (v) => {
            setVideo((cfg) => { cfg.muted = v; });
        })));
        root.appendChild(this._section('音量', this._num(video.volume ?? 1, 0.05, (v) => {
            setVideo((cfg) => { cfg.volume = Math.max(0, Math.min(1, v)); });
        })));
        root.appendChild(this._section('屏幕亮度', this._num(video.emissiveIntensity ?? 1.8, 0.1, (v) => {
            setVideo((cfg) => { cfg.emissiveIntensity = Math.max(0, v); });
        })));
    }

    private _renderImagePanelMarker(root: HTMLElement, id: string, o: SceneObject): void {
        const panel = o.marker.kind === 'imagePanel'
            ? o.marker
            : { kind: 'imagePanel', url: '', scale: DEFAULT_IMAGE_PANEL_SCALE, autoRotate: false, rotationSpeed: DEFAULT_IMAGE_PANEL_ROTATION_SPEED } as ImagePanelMarker;
        const setPanel = (fn: (v: ImagePanelMarker) => void) => {
            this._mut.mutate(id, (x) => {
                if (x.marker.kind !== 'imagePanel') return;
                fn(x.marker);
            });
            this._mut.refresh(id);
            this._mut.changed();
        };

        root.appendChild(this._section('面板图片', this._imageSelect(panel.url, (v) => {
            setPanel((cfg) => { cfg.url = v; });
        })));
        root.appendChild(this._section('自动旋转', this._checkbox(panel.autoRotate ?? false, (v) => {
            setPanel((cfg) => { cfg.autoRotate = v; });
        })));
        root.appendChild(this._section('旋转速度', this._num(panel.rotationSpeed ?? DEFAULT_IMAGE_PANEL_ROTATION_SPEED, 1, (v) => {
            setPanel((cfg) => { cfg.rotationSpeed = v; });
        })));
        root.appendChild(this._section('旋转轴', this._imagePanelRotationAxisSelect(panel.rotationAxis ?? 'y', (v) => {
            setPanel((cfg) => { cfg.rotationAxis = v; });
        })));
    }

    private _renderSceneSpawn(): void {
        const root = document.createElement('div');
        root.className = 'ep-root';

        const hint = document.createElement('div');
        hint.className = 'ep-empty';
        hint.textContent = '未选中对象';
        root.appendChild(hint);

        const spawn = this._mut.getSceneSpawn();
        if (!spawn) {
            this._mount.appendChild(root);
            return;
        }

        root.appendChild(this._section('出生点位置 (X / Y / Z)', this._vec3(spawn.spawn, 0.1, (v) => {
            const cur = this._mut.getSceneSpawn();
            this._mut.setSceneSpawn(v, cur?.spawnAngles ?? spawn.spawnAngles);
        })));

        root.appendChild(this._section('出生点视角 (Pitch / Yaw)', this._vec2(spawn.spawnAngles, (v) => {
            const cur = this._mut.getSceneSpawn();
            this._mut.setSceneSpawn(cur?.spawn ?? spawn.spawn, v);
        })));

        // Note: "使用当前位置" removed — the top bar's 设为出生点 button covers it.

        const skybox = this._mut.getSceneSkybox();
        const skyboxKind = skybox?.type ?? 'none';
        const skyboxIntensity = skybox?.intensity ?? 1;
        const skyboxRotation = skybox?.rotationYaw ?? 0;
        const mobileSkybox = this._mut.getSceneMobileSkybox();
        const mobileSkyboxKind = mobileSkybox?.type ?? 'none';
        const mobileSkyboxIntensity = mobileSkybox?.intensity ?? skyboxIntensity;
        const mobileSkyboxRotation = mobileSkybox?.rotationYaw ?? skyboxRotation;

        root.appendChild(this._section('桌面天空盒贴图', this._skyboxSelect(skybox, (config) => {
            if (!config) {
                this._mut.setSceneSkybox(null);
                return;
            }
            this._mut.setSceneSkybox({
                ...config,
                intensity: this._mut.getSceneSkybox()?.intensity ?? skyboxIntensity,
                rotationYaw: this._mut.getSceneSkybox()?.rotationYaw ?? skyboxRotation
            });
        })));

        root.appendChild(this._section('桌面天空盒强度', this._num(skyboxIntensity, 0.1, (v) => {
            const cur = this._mut.getSceneSkybox();
            if (!cur || skyboxKind === 'none') return;
            this._mut.setSceneSkybox({ ...cur, intensity: v });
        })));

        root.appendChild(this._section('桌面天空盒旋转', this._skyboxRotationInput(skyboxRotation, (v) => {
            const cur = this._mut.getSceneSkybox();
            if (!cur || skyboxKind === 'none') return;
            this._mut.setSceneSkybox({ ...cur, rotationYaw: v });
        })));

        root.appendChild(this._section('手机天空盒贴图', this._skyboxSelect(mobileSkybox, (config) => {
            if (!config) {
                this._mut.setSceneMobileSkybox(null);
                return;
            }
            this._mut.setSceneMobileSkybox({
                ...config,
                intensity: this._mut.getSceneMobileSkybox()?.intensity ?? mobileSkyboxIntensity,
                rotationYaw: this._mut.getSceneMobileSkybox()?.rotationYaw ?? mobileSkyboxRotation
            });
        })));

        root.appendChild(this._section('手机天空盒强度', this._num(mobileSkyboxIntensity, 0.1, (v) => {
            const cur = this._mut.getSceneMobileSkybox();
            if (!cur || mobileSkyboxKind === 'none') return;
            this._mut.setSceneMobileSkybox({ ...cur, intensity: v });
        })));

        root.appendChild(this._section('手机天空盒旋转', this._skyboxRotationInput(mobileSkyboxRotation, (v) => {
            const cur = this._mut.getSceneMobileSkybox();
            if (!cur || mobileSkyboxKind === 'none') return;
            this._mut.setSceneMobileSkybox({ ...cur, rotationYaw: v });
        })));

        this._mount.appendChild(root);
    }

    private _skyboxSelect(value: SceneSkyboxConfig | null, on: (v: SceneSkyboxConfig | null) => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';
        const configs = new Map<string, SceneSkyboxConfig>();

        const noneOpt = document.createElement('option');
        noneOpt.value = '';
        noneOpt.textContent = '无天空盒';
        sel.appendChild(noneOpt);

        const currentValue = value ? skyboxOptionValue(value) : '';
        if (value) {
            const cur = document.createElement('option');
            cur.value = currentValue;
            cur.textContent = value.type === 'texture'
                ? (value.url.split('/').pop() || value.url)
                : value.type === 'video'
                    ? `视频: ${value.url.split('/').pop() || value.url}`
                    : skyboxCubemapLabel(value.faces);
            cur.selected = true;
            sel.appendChild(cur);
            configs.set(currentValue, value);
        }

        getSkyboxList().then((items) => {
            for (const item of items) {
                const key = skyboxOptionValue(item.config);
                if (key === currentValue) continue;
                const opt = document.createElement('option');
                opt.value = key;
                opt.textContent = item.label;
                sel.appendChild(opt);
                configs.set(key, item.config);
            }
            if (items.length === 0 && !value) {
                const empty = document.createElement('option');
                empty.value = '';
                empty.textContent = '未找到 /assets/sky/ 可用天空盒';
                empty.disabled = true;
                sel.appendChild(empty);
            }

            // Append panoramic video options from /assets/sky/videos/.
            getSkyVideoList().then((urls) => {
                if (urls.length === 0) return;
                const group = document.createElement('optgroup');
                group.label = '全景视频';
                for (const u of urls) {
                    const key = `video:${u}`;
                    if (key === currentValue) continue;
                    const opt = document.createElement('option');
                    opt.value = key;
                    opt.textContent = u.split('/').pop() || u;
                    group.appendChild(opt);
                    configs.set(key, { type: 'video', url: u });
                }
                if (group.children.length > 0) sel.appendChild(group);
                sel.value = currentValue;
            });

            sel.value = currentValue;
        });

        sel.addEventListener('change', () => on(sel.value ? configs.get(sel.value) ?? null : null));
        return sel;
    }

    private _videoSelect(value: string, on: (v: string) => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';

        const noneOpt = document.createElement('option');
        noneOpt.value = '';
        noneOpt.textContent = '未选择视频';
        sel.appendChild(noneOpt);

        if (value) {
            const cur = document.createElement('option');
            cur.value = value;
            cur.textContent = value.split('/').pop() || value;
            cur.selected = true;
            sel.appendChild(cur);
        }

        getVideoList().then((urls) => {
            for (const u of urls) {
                if (u === value) continue;
                const opt = document.createElement('option');
                opt.value = u;
                opt.textContent = u.split('/').pop() || u;
                sel.appendChild(opt);
            }
            if (urls.length === 0 && !value) {
                const empty = document.createElement('option');
                empty.value = '';
                empty.textContent = '未找到 /assets/videos/ 可用视频';
                empty.disabled = true;
                sel.appendChild(empty);
            }
            sel.value = value || '';
        });

        sel.addEventListener('change', () => on(sel.value));
        return sel;
    }

    private _imageSelect(value: string, on: (v: string) => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';

        const noneOpt = document.createElement('option');
        noneOpt.value = '';
        noneOpt.textContent = '未选择默认图';
        sel.appendChild(noneOpt);

        if (value) {
            const cur = document.createElement('option');
            cur.value = value;
            cur.textContent = value.split('/').pop() || value;
            cur.selected = true;
            sel.appendChild(cur);
        }

        getImageList().then((urls) => {
            for (const u of urls) {
                if (u === value) continue;
                const opt = document.createElement('option');
                opt.value = u;
                opt.textContent = u.split('/').pop() || u;
                sel.appendChild(opt);
            }
            if (urls.length === 0 && !value) {
                const empty = document.createElement('option');
                empty.value = '';
                empty.textContent = '未找到 /assets/images/ 可用图片';
                empty.disabled = true;
                sel.appendChild(empty);
            }
            sel.value = value || '';
        });

        sel.addEventListener('change', () => on(sel.value));
        return sel;
    }

    private _header(o: SceneObject): HTMLElement {
        const h = document.createElement('div');
        h.className = 'ep-header';
        h.innerHTML = `<span class="ep-type">${o.type}</span><span class="ep-name">${o.name}</span>`;
        return h;
    }

    private _updateHeaderName(name: string): void {
        const el = this._mount.querySelector('.ep-name');
        if (el) el.textContent = name;
    }

    private _label(text: string): HTMLElement {
        const l = document.createElement('span');
        l.className = 'ep-label';
        l.textContent = text;
        return l;
    }

    private _hint(text: string): HTMLElement {
        const h = document.createElement('div');
        h.className = 'ep-empty';
        h.textContent = text;
        return h;
    }

    private _appendDivider(root: HTMLElement): void {
        const div = document.createElement('div');
        div.className = 'ep-divider';
        root.appendChild(div);
    }

    private _section(label: string, field: HTMLElement): HTMLElement {
        const s = document.createElement('div');
        s.className = 'ep-section';
        s.appendChild(this._label(label));
        s.appendChild(field);
        return s;
    }

    private _text(value: string, on: (v: string) => void): HTMLElement {
        const i = document.createElement('input');
        i.type = 'text'; i.value = value; i.className = 'ep-input ep-grow';
        i.addEventListener('input', () => on(i.value));
        return i;
    }

    private _num(value: number, step: number, on: (v: number) => void): HTMLElement {
        const i = document.createElement('input');
        i.type = 'number'; i.value = String(+value.toFixed(3)); i.step = String(step);
        i.className = 'ep-input';
        i.addEventListener('input', () => {
            const v = parseFloat(i.value);
            if (Number.isFinite(v)) on(v);
        });
        return i;
    }

    private _skyboxRotationInput(value: number, on: (v: number) => void): HTMLElement {
        const i = document.createElement('input');
        i.type = 'number';
        i.min = '0';
        i.max = '360';
        i.step = '1';
        i.value = String(clampSkyboxRotationYaw(value));
        i.className = 'ep-input';
        i.addEventListener('input', () => {
            const v = parseFloat(i.value);
            if (!Number.isFinite(v)) return;
            on(clampSkyboxRotationYaw(v));
        });
        return i;
    }

    private _imagePanelRotationAxisSelect(value: 'x' | 'y' | 'z', on: (v: 'x' | 'y' | 'z') => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ep-input ep-grow';
        const options: Array<['x' | 'y' | 'z', string]> = [
            ['x', 'X 轴'],
            ['y', 'Y 轴'],
            ['z', 'Z 轴']
        ];
        for (const [axis, label] of options) {
            const opt = document.createElement('option');
            opt.value = axis;
            opt.textContent = label;
            sel.appendChild(opt);
        }
        sel.value = value;
        sel.addEventListener('change', () => {
            if (sel.value === 'x' || sel.value === 'y' || sel.value === 'z') on(sel.value);
        });
        return sel;
    }

    private _checkbox(value: boolean, on: (v: boolean) => void): HTMLElement {
        const label = document.createElement('label');
        label.className = 'ep-check';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = value;
        input.addEventListener('change', () => on(input.checked));
        const text = document.createElement('span');
        text.textContent = value ? '开启' : '关闭';
        input.addEventListener('change', () => { text.textContent = input.checked ? '开启' : '关闭'; });
        label.appendChild(input);
        label.appendChild(text);
        return label;
    }

    private _vec3(value: [number, number, number], step: number, on: (v: [number, number, number]) => void, sink?: HTMLInputElement[]): HTMLElement;
    private _vec3(value: [number, number, number], on: (v: [number, number, number]) => void, sink?: HTMLInputElement[]): HTMLElement;
    private _vec3(
        value: [number, number, number],
        stepOrOn: number | ((v: [number, number, number]) => void),
        onMaybeSink?: ((v: [number, number, number]) => void) | HTMLInputElement[],
        sinkMaybe?: HTMLInputElement[]
    ): HTMLElement {
        const step = typeof stepOrOn === 'number' ? stepOrOn : 1;
        const on = typeof stepOrOn === 'function' ? stepOrOn : onMaybeSink as (v: [number, number, number]) => void;
        const sink = (Array.isArray(onMaybeSink) ? onMaybeSink : sinkMaybe) ?? [];
        const wrap = document.createElement('div');
        wrap.className = 'ep-vec3';
        const cur = [value[0], value[1], value[2]] as [number, number, number];
        for (let i = 0; i < 3; i++) {
            const idx = i;
            const inp = document.createElement('input');
            inp.type = 'number';
            inp.value = String(+value[i].toFixed(3));
            inp.step = String(step);
            inp.className = 'ep-input';
            inp.addEventListener('input', () => {
                const v = parseFloat(inp.value);
                if (Number.isFinite(v)) {
                    cur[idx] = v;
                    on([cur[0], cur[1], cur[2]]);
                }
            });
            sink.push(inp);
            wrap.appendChild(inp);
        }
        return wrap;
    }

    private _vec2(value: [number, number], on: (v: [number, number]) => void): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'ep-vec3';
        const cur = [value[0], value[1]] as [number, number];
        for (let i = 0; i < 2; i++) {
            const idx = i;
            const inp = document.createElement('input');
            inp.type = 'number';
            inp.value = String(+value[i].toFixed(3));
            inp.step = '1';
            inp.className = 'ep-input';
            inp.addEventListener('input', () => {
                const v = parseFloat(inp.value);
                if (Number.isFinite(v)) {
                    cur[idx] = v;
                    on([cur[0], cur[1]]);
                }
            });
            wrap.appendChild(inp);
        }
        return wrap;
    }

    /** Update the transform input values in place after a gizmo drag / undo /
     *  redo. Inputs the designer is currently typing into are left untouched. */
    private _refreshValues(): void {
        const id = this._state.selectedId;
        if (!id || id !== this._renderedId) return;
        const o = this._getMgr()?.get(id);
        if (!o) return;
        this._setVec(this._posInputs, o.transform.position);
        this._setVec(this._rotInputs, o.transform.rotation);
        const sv = markerScaleVec(o.marker);
        this._setVec(this._scaleInputs, sv);
    }

    private _setVec(inputs: HTMLInputElement[], v: [number, number, number]): void {
        for (let i = 0; i < 3; i++) {
            const inp = inputs[i];
            if (inp && document.activeElement !== inp) {
                inp.value = String(+v[i].toFixed(3));
            }
        }
    }

    destroy(): void {
        this._unsubscribe();
        this._unsubscribeTransform();
        this._mount.innerHTML = '';
    }
}
